import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { API_BASE_URL } from '../../utils/apiConfig';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface VerificationData {
  userId: string;
  name: string;
  displayName: string;
  email: string;
  status: string;
  token: string;
}

export default function VerifyPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [verificationData, setVerificationData] = useState<VerificationData | null>(null);
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    const verifyToken = async () => {
      try {
        const userId = searchParams.get('userId');
        const token = searchParams.get('token');

        if (!userId || !token) {
          toast.error('Invalid verification link');
          navigate('/login');
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/auth/verify?userId=${userId}&token=${token}`);

        if (!response.ok) {
          const errorData = await response.json();
          toast.error(errorData.error || 'Invalid or expired verification link');
          navigate('/login');
          return;
        }

        const data = await response.json();
        setVerificationData(data.data);
      } catch (error) {
        console.error('Error verifying token:', error);
        toast.error('Failed to verify your account');
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [searchParams, navigate]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !verificationData) {
      return;
    }

    try {
      setVerifying(true);

      const apiUrl = API_BASE_URL;
      const response = await fetch(`${apiUrl}/api/auth/setup-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userId: verificationData.userId,
          token: verificationData.token,
          password: formData.password,
          confirmPassword: formData.confirmPassword
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to set password');
      }

      toast.success('Password set successfully! You can now login to your account.');
      navigate('/login');
    } catch (error: any) {
      console.error('Error setting password:', error);
      toast.error(error.message || 'Failed to set password');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-6 text-zinc-500 font-black uppercase tracking-widest text-xs">Verifying profile</p>
        </div>
      </div>
    );
  }

  if (!verificationData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 font-sans">
      <div className="max-w-md w-full">
        <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl shadow-zinc-200 border border-zinc-100">
          <div className="text-center mb-10">
            <div className="flex justify-center mb-10">
              <img src="/gympe.svg" alt="Gympe" className="h-10 w-auto" />
            </div>
            <div className="w-16 h-16 bg-zinc-950 rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-xl shadow-zinc-200">
              <svg className="h-8 w-8 text-[#AAD816]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="text-3xl font-black text-zinc-950 uppercase tracking-tight">
              Finish account
            </h2>
            <p className="mt-3 text-zinc-500 font-medium leading-relaxed">
              Welcome, <span className="text-zinc-900 font-bold">{verificationData.displayName || verificationData.name}</span>! <br />Let's secure your profile.
            </p>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="email" className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-1">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={verificationData.email}
                  disabled
                  className="w-full px-5 py-4 bg-zinc-100 border border-zinc-200 rounded-2xl text-zinc-400 cursor-not-allowed font-medium"
                />
              </div>

              <div className="space-y-1">
                <label htmlFor="password" className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-1">
                  Create Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className={`w-full px-5 py-4 bg-zinc-50 border rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium outline-none ${errors.password ? 'border-red-300' : 'border-zinc-200'
                    }`}
                  placeholder="Min. 8 characters"
                />
                {errors.password && (
                  <p className="mt-1 text-[10px] font-bold text-red-500 uppercase tracking-tight ml-1">{errors.password}</p>
                )}
              </div>

              <div className="space-y-1">
                <label htmlFor="confirmPassword" className="block text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-1">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className={`w-full px-5 py-4 bg-zinc-50 border rounded-2xl focus:ring-2 focus:ring-[#AAD816] transition-all duration-200 font-medium outline-none ${errors.confirmPassword ? 'border-red-300' : 'border-zinc-200'
                    }`}
                  placeholder="Re-enter password"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-[10px] font-bold text-red-500 uppercase tracking-tight ml-1">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={verifying}
                className="group relative w-full flex justify-center py-4 px-4 bg-zinc-950 hover:bg-zinc-900 text-white font-black uppercase tracking-widest rounded-2xl transition-all active:scale-[0.98] shadow-lg shadow-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {verifying ? (
                  <>
                    <div className="w-5 h-5 border-2 border-[#AAD816] border-t-transparent rounded-full animate-spin mr-3"></div>
                    Setting up...
                  </>
                ) : (
                  'Complete Account'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 